import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ComissaoMotoristaService from "../../services/resource/ComissaoMotoristaService";
import { useAuth } from "../../context/authContext";
import LogService from "../../services/resource/logService";
import { returnDate } from "../../utils/formatDate";
import FilialService from "../../services/resource/filialService";
import TabelaRotasFinalizadas from "./TabelaRotasFinalizadas";
import RotasGreenmile from "./RotasGreenmile";
import ComissaoService from "../../services/resource/ComissaoService";
import GreenmileService from "../../services/resource/GreenmileService";

const formatPercent = (value) =>
    value != null ? `${Number(value).toFixed(2)}%` : "—";

const TABS = [
    { id: "lista", label: "Acompanhamento", icon: "fa fa-list" },
    { id: "dashboard", label: "Dashboard", icon: "fa fa-bar-chart" },
    { id: "ranking", label: "Ranking", icon: "fa fa-trophy" },
    { id: "greenmile", label: "Base Greenmile", icon: "fa fa-location-arrow" },
];

const MESES = [
    { label: "Janeiro", value: 1 }, { label: "Fevereiro", value: 2 },
    { label: "Março", value: 3 }, { label: "Abril", value: 4 },
    { label: "Maio", value: 5 }, { label: "Junho", value: 6 },
    { label: "Julho", value: 7 }, { label: "Agosto", value: 8 },
    { label: "Setembro", value: 9 }, { label: "Outubro", value: 10 },
    { label: "Novembro", value: 11 }, { label: "Dezembro", value: 12 },
];

const ANOS = [{ label: "2026", value: 2026 }, { label: "2027", value: 2027 }];

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = "teal" }) {
    const colors = {
        teal: "bg-teal-50   border-teal-200   text-teal-700",
        blue: "bg-blue-50   border-blue-200   text-blue-700",
        amber: "bg-amber-50  border-amber-200  text-amber-700",
        rose: "bg-rose-50   border-rose-200   text-rose-700",
        violet: "bg-violet-50 border-violet-200 text-violet-700",
        green: "bg-green-50  border-green-200  text-green-700",
        orange: "bg-orange-50 border-orange-200 text-orange-700",
        gray: "bg-muted border-border text-foreground",
    };
    return (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${colors[color]}`}>
            <div className="text-xl mt-0.5 w-5 flex-shrink-0 text-center">
                <i className={icon} />
            </div>
            <div>
                <p className="text-xs font-medium opacity-70">{label}</p>
                <p className="text-lg font-bold leading-tight">{value}</p>
                {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

// ─── MiniBar ─────────────────────────────────────────────────────────────────
function MiniBar({ label, value, max, color = "#14b8a6", suffix = "" }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="w-32 truncate text-muted-foreground flex-shrink-0">{label}</span>
            <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
            <span className="w-20 text-right font-semibold text-foreground">
                {typeof value === "number"
                    ? value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
                    : value}
                {suffix}
            </span>
        </div>
    );
}

// ─── DonutGauge ───────────────────────────────────────────────────────────────
function DonutGauge({ percent, label, color = "#14b8a6" }) {
    const r = 40;
    const circ = 2 * Math.PI * r;
    const dash = circ * Math.min(percent / 100, 1);
    return (
        <div className="flex flex-col items-center">
            <div className="relative w-24 h-24">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
                    <circle
                        cx="50" cy="50" r={r} fill="none"
                        stroke={color} strokeWidth="10"
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-base font-bold" style={{ color }}>{percent.toFixed(1)}%</span>
                </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1 text-center">{label}</p>
        </div>
    );
}
function DashboardTab({ relatorio, comissoes }) {

    const stats = useMemo(() => {
        if (!relatorio) return null;

        const totalRotas = relatorio.totais?.totalGreenmile ?? 0;
        const totalCargas = relatorio.totais?.totalCargas ?? 0;

        const totalComImagem = relatorio.totalRotasComImagem ?? 0;
        const totalComCarga = relatorio.totalRotasComCarga ?? 0;

        // Prazo
        const prazoMap = Object.fromEntries(
            (relatorio.percentualPorPrazo || []).map(p => [p.prazo, p.quantidade])
        );

        const totalNoPrazo = prazoMap["NO_PRAZO"] ?? 0;
        const totalForaPrazo = prazoMap["FORA_DO_PRAZO"] ?? 0;
        const totalIndefinido = prazoMap["INDEFINIDO"] ?? 0;

        // Percentuais
        const pctNoPrazo = totalRotas > 0 ? (totalNoPrazo / totalRotas) * 100 : 0;
        const pctComImagem = totalRotas > 0 ? (totalComImagem / totalRotas) * 100 : 0;

        // Auxiliares (contagem)
        const auxMap = relatorio.quantidadeAuxiliares || [];

        const total1Aux = auxMap.filter(a => a.quantidade === 1).length;
        const total2Aux = auxMap.filter(a => a.quantidade === 2).length;

        // Por filial (já vem pronto)
        const porFilial = relatorio.rotasPorFilial || [];
        const maxFilial = Math.max(...porFilial.map(f => f.quantidade), 0);


        const top5 = [...comissoes]
            .sort((a, b) => (b.totalRotas ?? 0) - (a.totalRotas ?? 0))
            .slice(0, 5);

        return {
            totalRotas,
            totalCargas,
            totalComImagem,
            totalComCarga,

            totalNoPrazo,
            totalForaPrazo,
            totalIndefinido,

            pctNoPrazo,
            pctComImagem,

            total1Aux,
            total2Aux,

            porFilial,
            maxFilial,

            rotasVinculadas: relatorio.rotasVinculadas ?? 0,
            rotasNaoVinculadas: relatorio.rotasNaoVinculadas ?? 0,
            top5
        };
    }, [relatorio]);

    if (!relatorio) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-bar-chart text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível. Selecione um período.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 space-y-5">

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
                <StatCard icon="fa fa-road" label="Total Rotas" value={stats.totalRotas} color="teal" />
                <StatCard icon="fa fa-truck" label="Total Cargas" value={stats.totalCargas} color="blue" />
                <StatCard icon="fa fa-link" label="Com Carga" value={stats.totalComCarga} color="violet" />
                <StatCard icon="fa fa-camera" label="Com Imagem" value={stats.totalComImagem} color="blue" />
                <StatCard icon="fa fa-clock-o" label="No Prazo" value={stats.totalNoPrazo} color="green" />
                <StatCard icon="fa fa-times-circle" label="Fora Prazo" value={stats.totalForaPrazo} color="red" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

                {/* Gauges */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-4 flex items-center gap-2 text-foreground">
                        <i className="fa fa-pie-chart text-teal-600" /> Indicadores
                    </h3>
                    <div className="flex justify-around">
                        <DonutGauge percent={stats.pctNoPrazo} label="% No Prazo" color="#22c55e" />
                        <DonutGauge percent={stats.pctComImagem} label="% Com Imagem" color="#3b82f6" />
                    </div>
                </div>

                {/* Por filial */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-building text-teal-600" /> Rotas por Filial
                    </h3>
                    <div className="space-y-2.5">
                        {stats.porFilial
                            .sort((a, b) => b.quantidade - a.quantidade)
                            .map(f => (
                                <MiniBar
                                    key={f.nome}
                                    label={f.nome}
                                    value={f.quantidade}
                                    max={stats.maxFilial}
                                />
                            ))}
                    </div>
                </div>

                {/* Distribuições */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-pie-chart text-violet-600" /> Distribuição
                    </h3>
                    <div className="space-y-3 mt-1">
                        {[
                            { label: "1 Auxiliar", value: stats.total1Aux, max: stats.totalRotas, color: "#8b5cf6" },
                            { label: "2 Auxiliares", value: stats.total2Aux, max: stats.totalRotas, color: "#14b8a6" },
                            { label: "No Prazo", value: stats.totalNoPrazo, max: stats.totalRotas, color: "#22c55e" },
                            { label: "Com Imagem", value: stats.totalComImagem, max: stats.totalRotas, color: "#3b82f6" },
                        ].map(item => (
                            <div key={item.label}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-muted-foreground">{item.label}</span>
                                    <span className="font-semibold">{item.value}</span>
                                </div>
                                <div className="bg-muted rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className="h-1.5 rounded-full"
                                        style={{
                                            width: `${item.max > 0 ? Math.min((item.value / item.max) * 100, 100) : 0}%`,
                                            background: item.color,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Resumo */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-info-circle text-blue-600" /> Resumo
                    </h3>
                    <div className="space-y-2 text-xs">
                        {[
                            { label: "Rotas", value: stats.totalRotas, bold: true },
                            { label: "Cargas", value: stats.totalCargas },
                            { label: "Vinculadas", value: stats.rotasVinculadas },
                            { label: "Não vinculadas", value: stats.rotasNaoVinculadas },
                            { label: "1 Auxiliar", value: stats.total1Aux },
                            { label: "2 Auxiliares", value: stats.total2Aux },
                        ].map(item => (
                            <div key={item.label} className="flex justify-between">
                                <span className="text-muted-foreground">{item.label}</span>
                                <span className={item.bold ? "font-bold text-teal-700" : "font-medium text-foreground"}>
                                    {item.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-trophy text-amber-500" /> Top 5 Mais Rotas
                    </h3>
                    <div className="space-y-2">
                        {stats.top5.map((c, i) => (
                            <div key={c.funcionarioId} className="flex items-center gap-2 text-xs">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-white text-[10px] flex-shrink-0
                                    ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-muted text-muted-foreground"}`}>
                                    {i + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-medium text-foreground">{c.funcionarioNome}</p>
                                    <p className="text-muted-foreground truncate">{c.filialNome}</p>
                                </div>
                                <span className="font-bold text-teal-700 flex-shrink-0">
                                    {c.totalRotas ?? 0} rotas
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
// ─── Dashboard Tab ────────────────────────────────────────────────────────────
function DashboardTab2({ comissoes }) {
    const stats = useMemo(() => {
        if (!comissoes.length) return null;

        const sum = (key) => comissoes.reduce((a, c) => a + (c[key] ?? 0), 0);

        const totalRotas = sum("totalRotas");
        const totalEscalas = sum("quantidadesEscalasDeRota");
        const totalNoPrazo = sum("rotasNoPrazo");
        const totalComImagem = sum("rotasComImagem");
        const total24h = sum("rotas24Horas");
        const total48h = sum("rotas48Horas");
        const total1Aux = sum("rotas1Aux");
        const total2Aux = sum("rotas2Aux");
        const totalFrete = sum("rotasFrete");

        const mediaConclusao = comissoes.filter(c => c.percentualConclusao != null)
            .reduce((a, c, _, arr) => a + (c.percentualConclusao ?? 0) / arr.length, 0);
        const mediaGreenmile = comissoes.filter(c => c.percentualGreenmile != null)
            .reduce((a, c, _, arr) => a + (c.percentualGreenmile ?? 0) / arr.length, 0);

        const porFilial = comissoes.reduce((acc, c) => {
            const f = c.filialNome ?? "Sem filial";
            if (!acc[f]) acc[f] = { rotas: 0, escalas: 0 };
            acc[f].rotas += c.totalRotas ?? 0;
            acc[f].escalas += c.quantidadesEscalasDeRota ?? 0;
            return acc;
        }, {});

        const porCargo = comissoes.reduce((acc, c) => {
            const cargo = c.funcionarioCargo ?? "Outros";
            if (!acc[cargo]) acc[cargo] = { rotas: 0, count: 0 };
            acc[cargo].rotas += c.totalRotas ?? 0;
            acc[cargo].count++;
            return acc;
        }, {});

        const maxFilial = Math.max(...Object.values(porFilial).map(v => v.rotas));
        const maxCargo = Math.max(...Object.values(porCargo).map(v => v.rotas));

        const top5 = [...comissoes]
            .sort((a, b) => (b.totalRotas ?? 0) - (a.totalRotas ?? 0))
            .slice(0, 5);

        const pctNoPrazo = totalRotas > 0 ? (totalNoPrazo / totalRotas) * 100 : 0;
        const pctComImagem = totalRotas > 0 ? (totalComImagem / totalRotas) * 100 : 0;

        return {
            totalRotas, totalEscalas, totalNoPrazo, totalComImagem,
            total24h, total48h, total1Aux, total2Aux, totalFrete,
            mediaConclusao, mediaGreenmile,
            pctNoPrazo, pctComImagem,
            porFilial, porCargo, maxFilial, maxCargo,
            top5,
            totalFuncionarios: comissoes.length,
        };
    }, [comissoes]);

    if (!comissoes.length) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-bar-chart text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível. Selecione um período.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 space-y-5">

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
                <StatCard icon="fa fa-users" label="Funcionários" value={stats.totalFuncionarios} color="blue" />
                <StatCard icon="fa fa-road" label="Total Rotas" value={stats.totalRotas} color="teal" />
                <StatCard icon="fa fa-calendar" label="Escalas" value={stats.totalEscalas} color="violet" />
                <StatCard icon="fa fa-clock-o" label="No Prazo" value={stats.totalNoPrazo} color="green" />
                <StatCard icon="fa fa-camera" label="Com Imagem" value={stats.totalComImagem} color="blue" />
                <StatCard icon="fa fa-hourglass-1" label="24 Horas" value={stats.total24h} color="amber" />
                <StatCard icon="fa fa-hourglass-2" label="48 Horas" value={stats.total48h} color="orange" />
                <StatCard icon="fa fa-truck" label="Frete" value={stats.totalFrete} color="gray" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

                {/* Gauges: conclusão e greenmile */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-4 flex items-center gap-2 text-foreground">
                        <i className="fa fa-check-circle text-teal-600" /> Médias de Desempenho
                    </h3>
                    <div className="flex justify-around">
                        <DonutGauge percent={stats.mediaConclusao} label="% Conclusão" color="#14b8a6" />
                        <DonutGauge percent={stats.mediaGreenmile} label="% Greenmile" color="#3b82f6" />
                        <DonutGauge percent={stats.pctNoPrazo} label="% No Prazo" color="#22c55e" />
                    </div>
                </div>

                {/* Por filial */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-building text-teal-600" /> Rotas por Filial
                    </h3>
                    <div className="space-y-2.5">
                        {Object.entries(stats.porFilial)
                            .sort((a, b) => b[1].rotas - a[1].rotas)
                            .map(([nome, { rotas }]) => (
                                <MiniBar key={nome} label={nome} value={rotas} max={stats.maxFilial} />
                            ))}
                    </div>
                </div>

                {/* Por cargo */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-id-badge text-blue-600" /> Rotas por Cargo
                    </h3>
                    <div className="space-y-2.5">
                        {Object.entries(stats.porCargo)
                            .sort((a, b) => b[1].rotas - a[1].rotas)
                            .map(([cargo, { rotas }]) => (
                                <MiniBar key={cargo} label={cargo} value={rotas} max={stats.maxCargo} color="#3b82f6" />
                            ))}
                    </div>
                </div>

                {/* Top 5 */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-trophy text-amber-500" /> Top 5 Mais Rotas
                    </h3>
                    <div className="space-y-2">
                        {stats.top5.map((c, i) => (
                            <div key={c.funcionarioId} className="flex items-center gap-2 text-xs">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-white text-[10px] flex-shrink-0
                                    ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-muted text-muted-foreground"}`}>
                                    {i + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-medium text-foreground">{c.funcionarioNome}</p>
                                    <p className="text-muted-foreground truncate">{c.filialNome}</p>
                                </div>
                                <span className="font-bold text-teal-700 flex-shrink-0">
                                    {c.totalRotas ?? 0} rotas
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Distribuição aux / prazo / imagem */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-pie-chart text-violet-600" /> Distribuição de Rotas
                    </h3>
                    <div className="space-y-3 mt-1">
                        {[
                            { label: "Com 1 Auxiliar", value: stats.total1Aux, max: stats.totalRotas, color: "#8b5cf6" },
                            { label: "Com 2 Auxiliares", value: stats.total2Aux, max: stats.totalRotas, color: "#14b8a6" },
                            { label: "No Prazo", value: stats.totalNoPrazo, max: stats.totalRotas, color: "#22c55e" },
                            { label: "Com Imagem", value: stats.totalComImagem, max: stats.totalRotas, color: "#3b82f6" },
                            { label: "24 Horas", value: stats.total24h, max: stats.totalEscalas, color: "#f59e0b" },
                            { label: "48 Horas", value: stats.total48h, max: stats.totalEscalas, color: "#f97316" },
                        ].map(item => (
                            <div key={item.label}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-muted-foreground">{item.label}</span>
                                    <span className="font-semibold">{item.value}</span>
                                </div>
                                <div className="bg-muted rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className="h-1.5 rounded-full"
                                        style={{
                                            width: `${item.max > 0 ? Math.min((item.value / item.max) * 100, 100) : 0}%`,
                                            background: item.color,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Resumo geral */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-info-circle text-blue-600" /> Resumo Geral
                    </h3>
                    <div className="space-y-2 text-xs">
                        {[
                            { label: "Total de rotas", value: stats.totalRotas, bold: true },
                            { label: "Total de escalas", value: stats.totalEscalas },
                            { label: "No prazo", value: stats.totalNoPrazo },
                            { label: "Com imagem", value: stats.totalComImagem },
                            { label: "24 horas", value: stats.total24h },
                            { label: "48 horas", value: stats.total48h },
                            { label: "Com 1 auxiliar", value: stats.total1Aux },
                            { label: "Com 2 auxiliares", value: stats.total2Aux },
                            { label: "Frete", value: stats.totalFrete },
                            { label: "Média % conclusão", value: formatPercent(stats.mediaConclusao) },
                            { label: "Média % greenmile", value: formatPercent(stats.mediaGreenmile) },
                        ].map(item => (
                            <div key={item.label} className="flex justify-between">
                                <span className="text-muted-foreground">{item.label}</span>
                                <span className={item.bold ? "font-bold text-teal-700" : "font-medium text-foreground"}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

// ─── Ranking Tab ──────────────────────────────────────────────────────────────
function RankingTab({ comissoes }) {
    const [sortKey, setSortKey] = useState("totalRotas");
    const [filterCargo, setFilterCargo] = useState("todos");

    const cargos = useMemo(() => {
        const set = new Set(comissoes.map(c => c.funcionarioCargo ?? "Outros"));
        return ["todos", ...Array.from(set)];
    }, [comissoes]);

    const ranked = useMemo(() => {
        const list = filterCargo === "todos"
            ? comissoes
            : comissoes.filter(c => c.funcionarioCargo === filterCargo);

        const getVal = (c) => {
            if (sortKey === "totalRotas") return c.totalRotas ?? 0;
            if (sortKey === "quantidadesEscalas") return c.quantidadesEscalasDeRota ?? 0;
            if (sortKey === "rotasNoPrazo") return c.rotasNoPrazo ?? 0;
            if (sortKey === "rotasComImagem") return c.rotasComImagem ?? 0;
            if (sortKey === "rotas24Horas") return c.rotas24Horas ?? 0;
            if (sortKey === "percentualConclusao") return c.percentualConclusao ?? 0;
            if (sortKey === "percentualGreenmile") return c.percentualGreenmile ?? 0;
            return 0;
        };

        return [...list].sort((a, b) => getVal(b) - getVal(a));
    }, [comissoes, sortKey, filterCargo]);

    const getDisplayValue = (c) => {
        if (sortKey === "totalRotas") return `${c.totalRotas ?? 0} rotas`;
        if (sortKey === "quantidadesEscalas") return `${c.quantidadesEscalasDeRota ?? 0} escalas`;
        if (sortKey === "rotasNoPrazo") return `${c.rotasNoPrazo ?? 0} rotas`;
        if (sortKey === "rotasComImagem") return `${c.rotasComImagem ?? 0} rotas`;
        if (sortKey === "rotas24Horas") return `${c.rotas24Horas ?? 0} rotas`;
        if (sortKey === "percentualConclusao") return formatPercent(c.percentualConclusao);
        if (sortKey === "percentualGreenmile") return formatPercent(c.percentualGreenmile);
        return "—";
    };

    const medalColors = ["bg-amber-400", "bg-gray-300", "bg-orange-400"];

    if (!comissoes.length) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-trophy text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4">
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <div className="flex gap-2 flex-wrap">
                    {[
                        { key: "totalRotas", label: "Total Rotas" },
                        { key: "quantidadesEscalas", label: "Escalas" },
                        { key: "rotasNoPrazo", label: "No Prazo" },
                        { key: "rotasComImagem", label: "Com Imagem" },
                        { key: "rotas24Horas", label: "24 Horas" },
                        { key: "percentualConclusao", label: "% Conclusão" },
                        { key: "percentualGreenmile", label: "% Greenmile" },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => setSortKey(opt.key)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150
                                ${sortKey === opt.key
                                    ? "bg-teal-600 text-white border-teal-600"
                                    : "bg-card text-muted-foreground border-border hover:border-teal-300"}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <select
                    value={filterCargo}
                    onChange={e => setFilterCargo(e.target.value)}
                    className="ml-auto text-xs border border-input rounded-lg px-3 py-1.5 bg-card text-foreground"
                >
                    {cargos.map(c => (
                        <option key={c} value={c}>{c === "todos" ? "Todos os Cargos" : c}</option>
                    ))}
                </select>
            </div>

            <div className="space-y-2">
                {ranked.map((c, i) => (
                    <div key={c.funcionarioId}
                        className="flex items-center gap-3 bg-card border border-border rounded-xl p-3 shadow-card hover:shadow-soft transition-shadow duration-150"
                    >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0
                            ${i < 3 ? medalColors[i] : "bg-muted text-muted-foreground"}`}>
                            {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-foreground truncate">{c.funcionarioNome}</p>
                            <p className="text-xs text-muted-foreground">{c.filialNome} · {c.funcionarioCargo} · {c.funcionarioCodigo}</p>
                        </div>
                        <div className="text-right flex-shrink-0 space-y-0.5">
                            <p className="font-bold text-teal-700 text-sm">{getDisplayValue(c)}</p>
                            <p className="text-[10px] text-muted-foreground">
                                {c.totalRotas ?? 0} rotas · {formatPercent(c.percentualConclusao)} concl.
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function RotasFinalizadas() {
    const { user } = useAuth();
    const isRoleComissao = user.perfis.includes("COMISSOES");

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        if (mesInicial === 12) { mesInicial = 1; anoInicial += 1; }
        else { mesInicial += 1; }
    }

    const comissaoService = useMemo(() => new ComissaoService(), []);
    const greenmileService = useMemo(() => new GreenmileService(), []);
    const filialService = useMemo(() => new FilialService(), []);
    const logService = useMemo(() => new LogService(), []);

    const [activeTab, setActiveTab] = useState("lista");
    const [loading, setLoading] = useState(true);
    const [ano, setAno] = useState(anoInicial);
    const [mes, setMes] = useState(mesInicial);
    const [log, setLog] = useState({ data: null });
    const [comissoes, setComissoes] = useState([]);
    const [rotasGreenmile, setRotasGreenmile] = useState([]);
    const [relatorioGreenmile, setRelatorioGreenmile] = useState({});
    const [filiais, setFiliais] = useState([]);
    const [filialId, setFilialId] = useState(null);

    useEffect(() => { buscarLogs(); }, []);

    useEffect(() => {
        carregarDados();
    }, [ano, mes, filialId]);

    const carregarDados = () => {
        setLoading(true);

        const filialFiltro = isRoleComissao ? user.filialId : filialId;

        Promise.all([
            greenmileService.relatorio({ ano, mes, filialId: filialFiltro, }),
            greenmileService.listar({ ano, mes, filialId: isRoleComissao ? user.filialId : filialId, }),
            filialService.listarComComissao(ano, mes),
            comissaoService.listar({ ano, mes, filialId: isRoleComissao ? user.filialId : filialId, })
        ])
            .then(([relatorioRes, rotasRes, filiaisRes, comissoesRes]) => {
                setRelatorioGreenmile(relatorioRes.data);
                setRotasGreenmile(rotasRes.data);
                setFiliais(filiaisRes.data);
                setComissoes(comissoesRes.data)
            })
            .catch(e => {
                console.error("Erro ao carregar dashboard:", e);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const buscarLogs = () => {
        logService.buscarLogPorModulo("GERACAO_COMISSAO")
            .then(res => setLog(res.data))
            .catch(e => console.error(e));
    };

    return (
        <div className="bg-background">

            {/* Header */}
            <div className="bg-card border-b border-border px-4 pt-4">
                <div className="flex flex-wrap justify-between gap-3 mb-3">
                    <div>
                        <h1 className="font-display text-xl font-bold text-foreground">Rotas Finalizadas por Funcionário</h1>
                        <p className="text-sm text-muted-foreground">
                            Acompanhamento de rotas, escalas, prazo e imagens
                            {log.data && (
                                <span className="text-xs fade-in"> | Atualizado {returnDate(log.data)}</span>
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card">
                        {!isRoleComissao && (
                            <select
                                value={filialId ?? ""}
                                onChange={e => setFilialId(e.target.value)}
                                className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                            >
                                <option value="">Todas Filiais</option>
                                {filiais.map(f => (
                                    <option key={f.id} value={f.id}>{f.nome}</option>
                                ))}
                            </select>
                        )}
                        <i className="fa fa-calendar-alt text-muted-foreground text-sm mr-1" />
                        <select
                            value={mes}
                            onChange={e => setMes(e.target.value)}
                            className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {MESES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                        <span className="text-border">|</span>
                        <select
                            value={ano}
                            onChange={e => setAno(Number(e.target.value))}
                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-0 -mb-px">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors duration-150 whitespace-nowrap
                                ${activeTab === tab.id
                                    ? "border-primary text-primary bg-primary/5"
                                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
                        >
                            <i className={tab.icon} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
                {activeTab === "lista" && (
                    <motion.div key="lista" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaRotasFinalizadas data={comissoes} loading={loading} ano={ano} mes={mes} />
                    </motion.div>
                )}
                {activeTab === "dashboard" && (
                    <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <DashboardTab relatorio={relatorioGreenmile} comissoes={comissoes} />
                    </motion.div>
                )}
                {activeTab === "ranking" && (
                    <motion.div key="ranking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <RankingTab comissoes={comissoes} />
                    </motion.div>
                )}
                {activeTab === "greenmile" && (
                    <motion.div key="greenmile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <RotasGreenmile rotas={rotasGreenmile} loading={loading} ano={ano} mes={mes} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
